
import { ConceptActivation } from "../../concept/ConceptActivation"
import { ValidityDeterminate } from "@fusion-advantage/backcompat"

const baseNodeTypes = {
	Program: 'Program',
	AssignmentStatement: 'AssignmentStatement',
	ConceptStatement: 'ConceptStatement',
	IfStatement: 'IfStatement',
	PrintStatement: 'PrintStatement',
	EmptyReturnStatement: 'EmptyReturnStatement',
	ValueReturnStatement: 'ValueReturnStatement',
	ValidationReturnStatement: 'ValidationReturnStatement',
	ActivationReturnStatement: 'ActivationReturnStatement',
	BinaryExpression: 'BinaryExpression',
	NumericLiteral: 'NumericLiteral',
	StringLiteral: 'StringLiteral',
	BooleanLiteral: 'BooleanLiteral',
	NullLiteral: 'NullLiteral',
	Identifier: 'Identifier',
	Array: 'Array',
	ForLoop: 'ForLoop',
	Constant: 'Constant',
	ContinueStatement: 'Continue',
} as const

import { functionNodeTypes } from '../functions/FunctionTypes'

export const NodeType = {
	...baseNodeTypes,
	...functionNodeTypes,
} as const

export type NodeTypeValue = typeof NodeType[keyof typeof NodeType]

interface ASTNodeBase<T extends NodeTypeValue> {
	type: T
	line: number
	column: number
	hasReturn?: boolean
}

export interface ProgramNode extends ASTNodeBase<typeof NodeType.Program> {
	body: ASTNode[]
}

export interface AssignmentStatementNode extends ASTNodeBase<typeof NodeType.AssignmentStatement> {
	identifier: string
	expression: ExpressionNode
}

export interface IfStatementNode extends ASTNodeBase<typeof NodeType.IfStatement> {
	condition: ExpressionNode
	consequent: ASTNode[]
	alternate: ASTNode[] | IfStatementNode | null
	guaranteedReturn: boolean
}

export interface PrintStatementNode extends ASTNodeBase<typeof NodeType.PrintStatement> {
	argument: ExpressionNode
}

export interface EmptyReturnStatementNode extends ASTNodeBase<typeof NodeType.EmptyReturnStatement> {}

export interface ValueReturnStatementNode extends ASTNodeBase<typeof NodeType.ValueReturnStatement> {
	value: ExpressionNode
	subconceptId?: ExpressionNode
}

export interface ValidationReturnStatementNode extends ASTNodeBase<typeof NodeType.ValidationReturnStatement> {
	validity: ValidityDeterminate
	message?: string
}

export interface ActivationReturnStatementNode extends ASTNodeBase<typeof NodeType.ActivationReturnStatement> {
	activation: ConceptActivation
}

export interface BinaryExpressionNode extends ASTNodeBase<typeof NodeType.BinaryExpression> {
	operator: string
	left: ExpressionNode
	right: ExpressionNode
}

export interface NumericLiteralNode extends ASTNodeBase<typeof NodeType.NumericLiteral> {
	value: number
}

export interface StringLiteralNode extends ASTNodeBase<typeof NodeType.StringLiteral> {
	value: string
}

export interface BooleanLiteralNode extends ASTNodeBase<typeof NodeType.BooleanLiteral> {
	value: boolean
}

export interface NullLiteralNode extends ASTNodeBase<typeof NodeType.NullLiteral> {
	value: null
}

export interface IdentifierNode extends ASTNodeBase<typeof NodeType.Identifier> {
	name: string
}

export interface ContinueStatementNode extends ASTNodeBase<typeof NodeType.ContinueStatement> {}

export interface ArrayLiteralNode extends ASTNodeBase<typeof NodeType.Array> {
	elements: ExpressionNode[]
}

export interface ForLoopNode extends ASTNodeBase<typeof NodeType.ForLoop> {
	loopVariable: string
	collection: ExpressionNode
	loopBody: ASTNode[]
}

export interface ConstantNode extends ASTNodeBase<typeof NodeType.Constant> {
	name: string
}

type DynamicFunctionType = typeof functionNodeTypes[keyof typeof functionNodeTypes]

export interface DynamicFunctionNode extends ASTNodeBase<DynamicFunctionType> {
	arguments: ExpressionNode[]
}

export type ASTNode = | ProgramNode | StatementNode | ExpressionNode
export type ReturnStatementNode = EmptyReturnStatementNode | ValueReturnStatementNode | ValidationReturnStatementNode | ActivationReturnStatementNode
export type StatementNode = AssignmentStatementNode | IfStatementNode | PrintStatementNode | EmptyReturnStatementNode | ValueReturnStatementNode | ValidationReturnStatementNode | ActivationReturnStatementNode | ForLoopNode | ContinueStatementNode
export type ExpressionNode = BinaryExpressionNode | NumericLiteralNode | IdentifierNode | StringLiteralNode | BooleanLiteralNode | NullLiteralNode | ArrayLiteralNode | ConstantNode | DynamicFunctionNode
export type ScalarExpressionNode = Exclude<ExpressionNode, ArrayLiteralNode>

export function getChildNodes(node: ASTNode): ASTNode[] {
	switch (node.type) {
		case NodeType.Program:
			return node.body
		case NodeType.IfStatement: {
			const children: ASTNode[] = [node.condition, ...node.consequent]
			if (node.alternate) {
				if (Array.isArray(node.alternate)) {
					children.push(...node.alternate)
				} else {
					children.push(node.alternate)
				}
			}
			return children
		}
		case NodeType.AssignmentStatement:
			return [node.expression]
		case NodeType.BinaryExpression:
			return [node.left, node.right]
		case NodeType.ValueReturnStatement:
			return node.subconceptId ? [node.value, node.subconceptId] : [node.value]
		case NodeType.PrintStatement:
			return [node.argument]
		case NodeType.Array:
			return node.elements
		case NodeType.ForLoop:
			return [node.collection, ...node.loopBody]
		case NodeType.EmptyReturnStatement:
		case NodeType.ValidationReturnStatement:
		case NodeType.ActivationReturnStatement:
		case NodeType.ContinueStatement:
		case NodeType.NumericLiteral:
		case NodeType.StringLiteral:
		case NodeType.BooleanLiteral:
		case NodeType.NullLiteral:
		case NodeType.Identifier:
		case NodeType.Constant:
			return []
		default:
			// Fallback for DynamicFunctionNode
			return node.arguments
	}
}
